import React, { useEffect, useState } from "react";
import { doc, setDoc, addDoc, collection, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, radius, spacing, typography } from "../../utils/designTokens";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../components/Toast";
import {
  TIPO_OBJECIONES, BLOQUES_CONFIG, CANALES, canalDeMaterial, plantillaBloques, bloqueVacio, buildBloquesHtml,
} from "../../utils/materialesBloques";
import { descargarFlyerPdf } from "../../utils/flyerExport";
import {
  loadContacto, Label, SecTitle, input, chkLabel, topbarStyle, tabsRow, tabBtn,
  backBtn, primaryBtn, ghostBtn, miniBtn, iconBtn, descMenu, descItem, infoBanner, boxContacto, boxPuntos,
} from "./materialesUi";
import {
  FaArrowLeft, FaSave, FaDownload, FaChevronDown, FaRegFilePdf, FaCode, FaTrash,
  FaPlus, FaArrowUp, FaArrowDown, FaRegCopy, FaCheck, FaPen, FaEye,
} from "react-icons/fa";

/**
 * Editor de materiales "de bloques" (guion comercial y banco de objeciones).
 * A diferencia del flyer/deck, acá el uso principal NO es exportar: es COPIAR
 * cada bloque al portapapeles para pegarlo en WhatsApp o mail. Por eso la vista
 * dominante es "Usar" (cards con botón Copiar) y la edición va en el aside.
 */
export default function BloquesEditor({ material, isMobile, isSuperAdmin, myEmail, onClose, onSaved, onDeleted }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = userEmail || auth.currentUser?.email || "";

  const tipo = material.tipo === TIPO_OBJECIONES ? TIPO_OBJECIONES : material.tipo;
  const cfg = BLOQUES_CONFIG[tipo] || BLOQUES_CONFIG.guion_comercial;

  const esGeneral = (material.scope || "general") !== "personal";
  const esBuiltin = !!material.builtin;
  const docId = esBuiltin ? null : material.id;
  const copiaAMios = esGeneral && !isSuperAdmin && (esBuiltin || !!docId);
  const puedeEliminar = !!docId && (isSuperAdmin || !esGeneral);

  const c0 = loadContacto(email);
  const [nombre, setNombre] = useState(material.nombre || cfg.nombre);
  const [bloques, setBloques] = useState((material.bloques || bloqueVacio()).map((b) => ({ ...b })));
  const [canal, setCanal] = useState(canalDeMaterial(material));
  const [agente] = useState({ name: c0.name || userName || "", email: c0.email || email, tel: c0.tel || "" });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("usar"); // mobile: usar | editar
  const [descOpen, setDescOpen] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [copiadoId, setCopiadoId] = useState(null);

  useEffect(() => { setCopiadoId(null); }, [tab]);

  // ── Operaciones sobre bloques ──
  const upd = (id, patch) => setBloques((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const del = (id) => setBloques((bs) => bs.filter((b) => b.id !== id));
  const move = (id, dir) => setBloques((bs) => {
    const i = bs.findIndex((b) => b.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= bs.length) return bs;
    const next = bs.slice(); [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const add = () => setBloques((bs) => [...bs, { id: `b${Date.now()}`, titulo: "", cuerpo: "", extra: "", incluido: true }]);
  // Restaurar vuelve a la plantilla del playbook si el material nació de una.
  const restaurar = () => setBloques(plantillaBloques(material.plantillaId) || bloqueVacio());

  const incluidos = bloques.filter((b) => b.incluido && ((b.titulo || "").trim() || (b.cuerpo || "").trim()));

  async function copiarBloque(b) {
    try {
      await navigator.clipboard.writeText(b.cuerpo || "");
      setCopiadoId(b.id);
      setTimeout(() => setCopiadoId((v) => (v === b.id ? null : v)), 2000);
    } catch {
      showToast("No se pudo copiar. Probá de nuevo.", "error");
    }
  }

  async function copiarTodo() {
    try {
      const txt = incluidos.map((b) => `${b.titulo}\n${b.cuerpo}`).join("\n\n——\n\n");
      await navigator.clipboard.writeText(txt);
      showToast("Todos los bloques copiados.", "success");
    } catch {
      showToast("No se pudo copiar.", "error");
    }
  }

  async function guardar() {
    if (!nombre.trim()) { showToast("Poné un nombre al material.", "error"); return; }
    setSaving(true);
    try {
      let scope, owner, id;
      if (copiaAMios) { scope = "personal"; owner = myEmail; id = null; }
      else if (docId) {
        scope = material.scope || (esGeneral ? "general" : "personal");
        owner = scope === "personal" ? (material.owner || myEmail) : "";
        id = docId;
      } else {
        scope = material.scope || (isSuperAdmin ? "general" : "personal");
        owner = scope === "personal" ? myEmail : "";
        id = null;
      }
      const payload = {
        nombre: nombre.trim(), tipo, bloques, canal,
        plantillaId: material.plantillaId || null,
        scope, owner, updatedAt: serverTimestamp(), updatedBy: email,
      };
      if (!id) {
        payload.createdAt = serverTimestamp();
        payload.createdBy = email;
        const ref = await addDoc(collection(db, "materiales"), payload);
        id = ref.id;
      } else {
        await setDoc(doc(db, "materiales", id), payload, { merge: true });
      }
      showToast(copiaAMios ? "Guardado en Mis materiales." : "Material guardado.", "success");
      onSaved(id, { ...payload });
    } catch (e) {
      showToast(e.message || "No se pudo guardar.", "error");
    }
    setSaving(false);
  }

  async function eliminar() {
    if (!docId) { onClose(); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Eliminar este material?")) return;
    try { await deleteDoc(doc(db, "materiales", docId)); showToast("Material eliminado.", "success"); onDeleted(docId); }
    catch (e) { showToast(e.message || "No se pudo eliminar.", "error"); }
  }

  const html = () => buildBloquesHtml({ tipo, nombre, bloques, agente });
  const slugName = () => (nombre || cfg.nombre).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "material";

  function descargarHtml() {
    const blob = new Blob([html()], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `habitia-${slugName()}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast("HTML descargado.", "success");
  }
  async function doExport(t) {
    setDescOpen(false);
    if (t === "html") return descargarHtml();
    setExportando(true);
    try {
      await descargarFlyerPdf(html(), `habitia-${slugName()}.pdf`);
      showToast("PDF descargado.", "success");
    } catch {
      showToast("No se pudo generar el archivo. Probá de nuevo.", "error");
    }
    setExportando(false);
  }

  const descargarBtn = (
    <div style={{ position: "relative" }}>
      <button onClick={() => setDescOpen((o) => !o)} disabled={exportando} style={ghostBtn} aria-haspopup="menu" aria-expanded={descOpen}>
        <FaDownload size={12} /> {exportando ? "Generando…" : "Descargar"} <FaChevronDown size={9} />
      </button>
      {descOpen && (
        <>
          <div onClick={() => setDescOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div role="menu" style={descMenu}>
            <button onClick={() => doExport("pdf")} style={descItem}><FaRegFilePdf size={12} color={colors.error} /> PDF</button>
            <button onClick={() => doExport("html")} style={descItem}><FaCode size={12} color={colors.gray600} /> HTML</button>
          </div>
        </>
      )}
    </div>
  );

  // ── Vista "Usar": cards con Copiar por bloque ──
  const usarPane = (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", background: colors.bgMain, padding: isMobile ? spacing[3] : spacing[5] }}>
      <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gap: spacing[3] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          <span style={{ flex: 1, fontSize: typography.sizes.sm, color: colors.gray600 }}>
            Tocá <b>Copiar</b> y pegalo en WhatsApp o mail. Los [corchetes] se reemplazan antes de enviar.
          </span>
          <button onClick={copiarTodo} style={miniBtn}><FaRegCopy size={11} /> Copiar todo</button>
        </div>
        {incluidos.map((b, i) => (
          <div key={b.id} style={{ background: colors.white, borderRadius: radius.md, padding: spacing[4], boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: spacing[2], marginBottom: spacing[2] }}>
              <span style={{ fontSize: typography.sizes.sm, color: colors.primary, flexShrink: 0, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div style={{ flex: 1, minWidth: 0, fontSize: typography.sizes.lg, color: colors.black }}>{b.titulo || "(sin título)"}</div>
              <button
                onClick={() => copiarBloque(b)}
                style={{ ...miniBtn, flexShrink: 0, ...(copiadoId === b.id ? { borderColor: colors.success, color: colors.successDark, background: colors.successLight } : {}) }}
                aria-label={`Copiar ${b.titulo || "bloque"}`}
              >
                {copiadoId === b.id ? <FaCheck size={11} /> : <FaRegCopy size={11} />} {copiadoId === b.id ? "Copiado" : "Copiar"}
              </button>
            </div>
            <div style={{ fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{b.cuerpo}</div>
            {b.extra && (
              <div style={{ marginTop: spacing[2], fontSize: typography.sizes.sm, color: colors.gray600, borderLeft: `3px solid ${colors.primaryLight}`, paddingLeft: spacing[2], whiteSpace: "pre-wrap" }}>
                {b.extra}
              </div>
            )}
          </div>
        ))}
        {incluidos.length === 0 && (
          <div style={{ background: colors.white, borderRadius: radius.md, padding: spacing[5], textAlign: "center", color: colors.gray600, fontSize: typography.sizes.sm }}>
            No hay bloques incluidos. Pasá a "Editar" y agregá o marcá alguno.
          </div>
        )}
      </div>
    </div>
  );

  // ── Panel de edición ──
  const editorPanel = (
    <div style={{ display: "grid", gap: spacing[3] }}>
      {copiaAMios && (
        <div style={infoBanner}>Material <b>general</b> de Habitia. Al guardar se crea <b>tu copia</b> en “Mis materiales” (renombrala como quieras).</div>
      )}
      {esBuiltin && isSuperAdmin && (
        <div style={infoBanner}>Plantilla base. Al guardar se crea la versión <b>general</b> editable que ven todos los comerciales.</div>
      )}
      <section style={boxContacto}>
        <SecTitle color={colors.gray700}>Canal</SecTitle>
        <Label>Dónde se muestra en la biblioteca</Label>
        <select value={canal} onChange={(e) => setCanal(e.target.value)} aria-label="Canal del material" style={{ ...input, cursor: "pointer" }}>
          {CANALES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <p style={{ margin: "10px 0 0", fontSize: typography.sizes.xs, color: colors.gray600 }}>
          El PDF se exporta con tu contacto de Materiales ({agente.name || "sin nombre"} · {agente.email || "sin email"}).
        </p>
      </section>
      <section style={boxPuntos}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing[2] }}>
          <SecTitle color={colors.primary} style={{ marginBottom: 0 }}>Bloques ({incluidos.length})</SecTitle>
          <button onClick={restaurar} style={miniBtn}>Restaurar</button>
        </div>
        <div style={{ display: "grid", gap: spacing[2] }}>
          {bloques.map((b, i) => (
            <div key={b.id} style={{ border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[2], background: b.incluido ? colors.white : colors.gray50 }}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: 6, flexWrap: "wrap" }}>
                <label style={chkLabel}><input type="checkbox" checked={!!b.incluido} onChange={(e) => upd(b.id, { incluido: e.target.checked })} /> Incluir</label>
                <div style={{ flex: 1 }} />
                <button onClick={() => move(b.id, -1)} disabled={i === 0} style={iconBtn} aria-label="Subir"><FaArrowUp size={10} /></button>
                <button onClick={() => move(b.id, 1)} disabled={i === bloques.length - 1} style={iconBtn} aria-label="Bajar"><FaArrowDown size={10} /></button>
                <button onClick={() => del(b.id)} style={{ ...iconBtn, color: colors.error }} aria-label="Borrar"><FaTrash size={10} /></button>
              </div>
              <Label>{cfg.labelTitulo}</Label>
              <input value={b.titulo} onChange={(e) => upd(b.id, { titulo: e.target.value })} placeholder={cfg.labelTitulo} style={{ ...input, marginBottom: 6 }} />
              <Label>{cfg.labelCuerpo}</Label>
              <textarea value={b.cuerpo} onChange={(e) => upd(b.id, { cuerpo: e.target.value })} placeholder={cfg.labelCuerpo} rows={5} style={{ ...input, resize: "vertical", fontFamily: typography.fontFamily, marginBottom: 6 }} />
              <Label>{cfg.labelExtra}</Label>
              <textarea value={b.extra || ""} onChange={(e) => upd(b.id, { extra: e.target.value })} placeholder={cfg.labelExtra} rows={2} style={{ ...input, resize: "vertical", fontFamily: typography.fontFamily }} />
            </div>
          ))}
        </div>
        <button onClick={add} style={{ ...miniBtn, marginTop: spacing[2], width: "100%", justifyContent: "center" }}><FaPlus size={10} /> Agregar bloque</button>
      </section>
    </div>
  );

  const topbar = (
    <div style={topbarStyle}>
      <button onClick={onClose} style={backBtn} aria-label="Volver a materiales"><FaArrowLeft size={12} /> {isMobile ? "" : "Materiales"}</button>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del material" style={{ ...input, flex: 1, minWidth: 0, height: 38, background: colors.gray50 }} />
      <button onClick={guardar} disabled={saving} style={primaryBtn}><FaSave size={12} /> {saving ? "…" : (copiaAMios ? "Guardar copia" : "Guardar")}</button>
      {!isMobile && descargarBtn}
      {!isMobile && puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }} aria-label="Eliminar"><FaTrash size={12} /></button>}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {topbar}
        <div style={tabsRow}>
          <button onClick={() => setTab("usar")} style={tabBtn(tab === "usar")}><FaEye size={11} /> Usar</button>
          <button onClick={() => setTab("editar")} style={tabBtn(tab === "editar")}><FaPen size={11} /> Editar</button>
        </div>
        {tab === "usar" ? (
          usarPane
        ) : (
          <div style={{ padding: 14, overflowY: "auto" }}>
            {editorPanel}
            <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[4], alignItems: "center" }}>
              {descargarBtn}
              {puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }}><FaTrash size={12} /></button>}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {topbar}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {usarPane}
        <aside style={{ width: 380, flexShrink: 0, overflowY: "auto", borderLeft: `1px solid ${colors.gray200}`, background: colors.white, padding: spacing[4] }}>
          {editorPanel}
        </aside>
      </div>
    </div>
  );
}
